// next-app/src/hooks/use-auth.ts
'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth-store';
import { queryKeys } from './query-keys';
import type { Dealer, LoginResponse, AccountResponse } from '@/types';

// ============================================================================
// Login Mutation
// ============================================================================

interface LoginCredentials {
  email: string;
  password: string;
}

interface UseLoginOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for dealer login.
 *
 * Replaces the pattern:
 * ```
 * stores.ui.setIsLoggingIn(true);
 * const { response, result } = await dtg.doLogin(email, password);
 * stores.ui.setIsLoggingIn(false);
 * ```
 *
 * Usage:
 * ```tsx
 * const login = useLogin();
 * login.mutate({ email, password });
 * // login.isPending replaces isLoggingIn
 * ```
 */
export function useLogin(options: UseLoginOptions = {}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const setDealer = useAuthStore((state) => state.setDealer);
  const setDealerToken = useAuthStore((state) => state.setDealerToken);

  return useMutation({
    mutationFn: async ({ email, password }: LoginCredentials) => {
      const { response, result } = await api.login(email, password);

      if (!response.ok) {
        const errorMessage =
          (result as { message?: string })?.message ||
          'Login failed. Please check your credentials.';
        throw new Error(errorMessage);
      }

      return result as LoginResponse;
    },
    onSuccess: (data) => {
      // Update auth store with token and dealer info
      setDealerToken(data.token);
      setDealer(data.dealer);

      // Invalidate any cached auth-related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });

      // Redirect to events page (or saved redirect URL)
      const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAfterLogin');
      router.push(savedRedirect || '/events');

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useLogin] Login failed:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Signup Mutation
// ============================================================================

interface SignupData extends Partial<Dealer> {
  password: string;
}

interface UseSignupOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for dealer registration.
 *
 * Original pattern in src/services/dtgDealerPortal.ts:
 * - POST v1/auth/signup with dealer data + password
 * - Returns token and dealer on success
 */
export function useSignup(options: UseSignupOptions = {}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const setDealer = useAuthStore((state) => state.setDealer);
  const setDealerToken = useAuthStore((state) => state.setDealerToken);

  return useMutation({
    mutationFn: async (data: SignupData) => {
      const { password, ...dealerData } = data;
      const { response, result } = await api.signup(dealerData, password);

      if (!response.ok) {
        const errorMessage =
          (result as { message?: string })?.message || 'Registration failed. Please try again.';
        throw new Error(errorMessage);
      }

      return result as LoginResponse;
    },
    onSuccess: (data) => {
      setDealerToken(data.token);
      setDealer(data.dealer);
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });
      router.push('/events');
      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useSignup] Registration failed:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Logout Mutation
// ============================================================================

interface UseLogoutOptions {
  onSuccess?: () => void;
}

/**
 * Hook for dealer logout.
 *
 * Original pattern:
 * 1. POST to /logout endpoint
 * 2. Call stores.login.doLogout() to clear local state
 * 3. Redirect to login page
 */
export function useLogout(options: UseLogoutOptions = {}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const doLogout = useAuthStore((state) => state.doLogout);

  return useMutation({
    mutationFn: async () => {
      await api.logout();
    },
    onSuccess: () => {
      // Clear auth store (already done in api.logout, but be explicit)
      doLogout();

      // Clear all cached queries
      queryClient.clear();

      // Redirect to login
      router.push('/login');

      options.onSuccess?.();
    },
    onError: (error) => {
      // Even on error, clear local state and redirect
      console.error('[useLogout] Logout API error:', error);
      doLogout();
      queryClient.clear();
      router.push('/login');
    },
  });
}

// ============================================================================
// Forgot Password Mutation
// ============================================================================

interface UseForgotPasswordOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for requesting password reset email.
 *
 * Original: POST v1/auth/verify_email with { contact_email }
 */
export function useForgotPassword(options: UseForgotPasswordOptions = {}) {
  return useMutation({
    mutationFn: async (email: string) => {
      const { response, result } = await api.forgotPassword(email);

      if (!response.ok) {
        throw new Error('Failed to send password reset email.');
      }

      return result;
    },
    onSuccess: () => {
      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useForgotPassword] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Reset Password Mutation
// ============================================================================

interface ResetPasswordData {
  password: string;
  token: string;
}

interface UseResetPasswordOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for resetting password with token from email.
 *
 * Original: POST v1/auth/change_password with password in body, token in header
 */
export function useResetPassword(options: UseResetPasswordOptions = {}) {
  const router = useRouter();

  return useMutation({
    mutationFn: async ({ password, token }: ResetPasswordData) => {
      const { response, result } = await api.resetPassword(password, token);

      if (!response.ok) {
        throw new Error('Failed to reset password. Token may be expired.');
      }

      return result;
    },
    onSuccess: () => {
      router.push('/login');
      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useResetPassword] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Get Account Query
// ============================================================================

/**
 * Hook to fetch current dealer account.
 *
 * Original: GET v1/auth/account
 *
 * Used to refresh dealer info or verify session is still valid.
 */
export function useAccount() {
  const setDealer = useAuthStore((state) => state.setDealer);
  const token = useAuthStore((state) => state.dealerToken);

  return useQuery({
    queryKey: queryKeys.auth.account(),
    queryFn: async () => {
      const { response, result } = await api.getAccount();

      if (!response.ok) {
        throw new Error('Failed to fetch account');
      }

      // Update auth store with latest dealer info
      const data = result as AccountResponse;
      setDealer(data.dealer);

      return data;
    },
    // Only run if authenticated
    enabled: Boolean(token),
    // Refetch on mount to ensure fresh data
    refetchOnMount: true,
  });
}
